import React from 'react';
import PropTypes from 'prop-types';

import { strings, getCurrentLocale } from './../../../localization';
import Switch from './../../../components/common/Switch';
import ProgressOverlay from './../../../components/common/ProgressOverlay';
import { ALL_EVENT_NAMES, AUDIO_ALERT_TYPES } from './../../../data/globalsettings';

import { UPDATE_SETTINGS_TIMEOUT } from './GlobalSettings';

import BuildSettings from '../../../components/buildSettings';


const getSortedAudioAlertEvents = () => {
    const lang = getCurrentLocale();
    return [...ALL_EVENT_NAMES].sort((a, b) => {
        const aTitle = strings[`settings_global_events_${a.snakeName}_header`] || a.snakeName;
        const bTitle = strings[`settings_global_events_${b.snakeName}_header`] || b.snakeName;
        return aTitle.localeCompare(bTitle, lang);
    });
};


export default class DriverAlerts extends React.PureComponent {

    static propTypes = {
        audioNotificationsAvailabilityConfigAvailable: PropTypes.bool.isRequired,
        audioNotificationsAvailabilityPending: PropTypes.bool.isRequired,
        audioNotificationsAvailabilityEnabled: PropTypes.bool,
        audioNotificationsAvailabilityErrorMessage: PropTypes.string,
        applyAudioNotificationsAvailability: PropTypes.func.isRequired,
        timeoutAudioNotificationsAvailability: PropTypes.func.isRequired,

        expandOrCollapseStateForUIReferences: PropTypes.func.isRequired,
        driverAlertsExpandReference: PropTypes.string.isRequired,
        driverAlertsExpandReferenceIsExpanded: PropTypes.bool.isRequired,
        audioNotificationsExpandReference: PropTypes.string.isRequired,
        audioNotificationsExpandReferenceIsExpanded: PropTypes.bool.isRequired,

        globalSettingsPending: PropTypes.bool,
        enabledEvents: PropTypes.object.isRequired,
    }

    constructor(props) {

        super(props);

        this.applyAudioNotificationsAvailabilityTimeoutId = undefined;
        this.audioAlertTimeoutIds = {};
        this.visualAlertTimeoutIds = {};

    }

    componentWillUnmount() {
        clearTimeout(this.applyAudioNotificationsAvailabilityTimeoutId);
        Object.values(this.audioAlertTimeoutIds).forEach(id => clearTimeout(id));
        Object.values(this.visualAlertTimeoutIds).forEach(id => clearTimeout(id));
    }

    expandOrCollapseSectionsAndSubsections = (isExpanded) => {
        this.props.expandOrCollapseStateForUIReferences(isExpanded,[
            this.props.driverAlertsExpandReference,
            this.props.audioNotificationsExpandReference
        ]);
    }

    handleAudioAlertChange = (event, newAlertType) => {
        const pascalName = event.pascalName;
        const currentValue = this.props[`events${pascalName}AudioAlert`];
        if (newAlertType === currentValue) return;

        const timeoutKey = `audioAlert_${pascalName}`;
        clearTimeout(this.audioAlertTimeoutIds[timeoutKey]);

        this.audioAlertTimeoutIds[timeoutKey] = setTimeout(() => {
            this.props[`timeoutEvents${pascalName}AudioAlert`](currentValue);
        }, UPDATE_SETTINGS_TIMEOUT);

        this.props[`applyEvents${pascalName}AudioAlert`](newAlertType, currentValue)
            .then(
                () => { clearTimeout(this.audioAlertTimeoutIds[timeoutKey]); },
                () => { clearTimeout(this.audioAlertTimeoutIds[timeoutKey]); }
            );
    }

    handleVisualAlertChange = (event) => {
        const pascalName = event.pascalName;
        const currentValue = this.props[`events${pascalName}VisualAlert`];
        const currentAudioAlert = this.props[`events${pascalName}AudioAlert`];
        const newValue = !currentValue;
        const shouldResetAudio = newValue === false && currentAudioAlert === AUDIO_ALERT_TYPES.BEEP;

        const timeoutKey = `visualAlert_${pascalName}`;
        clearTimeout(this.visualAlertTimeoutIds[timeoutKey]);

        // If we're resetting audio from BEEP to NONE, clear any in-progress audio alert timeout
        if (shouldResetAudio) {
            const audioTimeoutKey = `audioAlert_${pascalName}`;
            clearTimeout(this.audioAlertTimeoutIds[audioTimeoutKey]);
        }

        this.visualAlertTimeoutIds[timeoutKey] = setTimeout(() => {
            this.props[`timeoutEvents${pascalName}VisualAlert`](currentValue, shouldResetAudio ? currentAudioAlert : undefined);
        }, UPDATE_SETTINGS_TIMEOUT);

        this.props[`applyEvents${pascalName}VisualAlert`](newValue, currentValue, currentAudioAlert)
            .then(
                () => { clearTimeout(this.visualAlertTimeoutIds[timeoutKey]); },
                () => { clearTimeout(this.visualAlertTimeoutIds[timeoutKey]); }
            );
    }

    renderAudioAlertRow = (event) => {
        const pascalName = event.pascalName;
        const audioAlert = this.props[`events${pascalName}AudioAlert`];
        const pending = this.props[`events${pascalName}AudioAlertPending`] || false;
        const errorMessage = this.props[`events${pascalName}AudioAlertErrorMessage`];
        const isSupported = audioAlert !== undefined;

        if (!isSupported) return null;

        const eventTitle = strings[`settings_global_events_${event.snakeName}_header`] || event.snakeName;

        const visualAlert = this.props[`events${pascalName}VisualAlert`];
        const visualAlertPending = this.props[`events${pascalName}VisualAlertPending`] || false;
        const visualAlertErrorMessage = this.props[`events${pascalName}VisualAlertErrorMessage`];
        const visualAlertSupported = visualAlert !== undefined;

        const eventEnabled = this.props[`events${pascalName}Enabled`];
        const eventDisabled = eventEnabled === false;

        return (
            <div key={event.snakeName} className={`audio-alert-row${eventDisabled ? ' audio-alert-row-disabled' : ''}`}>
                <div className="audio-alert-event-name">
                    {eventTitle}
                </div>
                {eventDisabled ?
                    <div className="alert alert-dark setting-locked">
                        {strings.settings_enable_event_for_driver_alerts}&nbsp;
                        <a onClick={() => { this.props.onNavigateToEventsSection(); }}
                            href="#component_document_id_events">
                            {strings.settings_global_go_to_the_section}
                        </a>
                    </div>
                :
                    <>
                    {[AUDIO_ALERT_TYPES.NONE, AUDIO_ALERT_TYPES.BEEP, AUDIO_ALERT_TYPES.VOICE].map(alertType => {
                        const beepDisabledByVisualAlert = alertType === AUDIO_ALERT_TYPES.BEEP && visualAlertSupported && visualAlert === false;
                        return (
                            <div key={alertType} className="audio-alert-radio-cell">
                                {alertType === AUDIO_ALERT_TYPES.BEEP && !visualAlertSupported && event.snakeName !== 'speeding'
                                    ? null
                                    : <label className="audio-alert-radio-label"
                                        title={beepDisabledByVisualAlert ? strings.settings_global_driver_alerts_beep_requires_visual_alert : undefined}>
                                        <input
                                            type="radio"
                                            name={`audio_alert_${event.snakeName}`}
                                            value={alertType}
                                            checked={audioAlert === alertType}
                                            disabled={eventDisabled || pending || beepDisabledByVisualAlert}
                                            onChange={() => this.handleAudioAlertChange(event, alertType)}
                                        />
                                    </label>
                                }
                            </div>
                        );
                    })}
                    <div className="visual-alert-switch-cell">
                        {visualAlertSupported &&
                            <>
                                <Switch
                                    onChange={() => this.handleVisualAlertChange(event)}
                                    checked={visualAlert || false}
                                    disabled={eventDisabled || visualAlertPending}
                                    smallFormat={true}
                                />
                                {visualAlertPending && <ProgressOverlay />}
                                {visualAlertErrorMessage && <span className="error-message">{visualAlertErrorMessage}</span>}
                            </>
                        }
                    </div>
                    {pending && <ProgressOverlay />}
                    {errorMessage && <span className="error-message">{errorMessage}</span>}
                    </>
                }
            </div>
        );
    }

    render() {
        const sortedEvents = getSortedAudioAlertEvents()
            .filter(event => this.props.enabledEvents[event.snakeName]);

        return (
            <div id="component_document_id_driver_alerts">
                <h3>
                    <button
                        className={this.props.driverAlertsExpandReferenceIsExpanded ? "settings-disclosure-wrapper" : "settings-disclosure-wrapper collapsed"}
                        onClick={() => {
                            this.expandOrCollapseSectionsAndSubsections(!this.props.driverAlertsExpandReferenceIsExpanded)}
                        }>
                        {strings.settings_global_driver_alerts_header}
                        <i className="material-icons disclosure">keyboard_arrow_up</i>
                    </button>
                </h3>
                <div>{strings.settings_global_driver_alerts_description}</div>
                {this.props.audioNotificationsAvailabilityConfigAvailable &&
                    <div className='setting-group-horiz'>
                        <div className='setting-group switch-group'>
                            <div>
                                <Switch
                                    onChange={() => {

                                        clearTimeout(this.applyAudioNotificationsAvailabilityTimeoutId);
                                        const audioNotificationsAvailabilityEnabled = this.props.audioNotificationsAvailabilityEnabled;
                                        this.applyAudioNotificationsAvailabilityTimeoutId = setTimeout(() => {this.props.timeoutAudioNotificationsAvailability(audioNotificationsAvailabilityEnabled)}, UPDATE_SETTINGS_TIMEOUT);

                                        this.props.applyAudioNotificationsAvailability(!this.props.audioNotificationsAvailabilityEnabled)
                                        .then(
                                            () => {clearTimeout(this.applyAudioNotificationsAvailabilityTimeoutId)},
                                            () => {clearTimeout(this.applyAudioNotificationsAvailabilityTimeoutId)}
                                        )
                                    }}
                                    checked={this.props.audioNotificationsAvailabilityEnabled || false}
                                    disabled={this.props.audioNotificationsAvailabilityPending}
                                    smallFormat={true}
                                />
                                <label disabled={this.props.audioNotificationsAvailabilityPending}>{this.props.audioNotificationsAvailabilityEnabled?
                                    strings.settings_global_driver_alerts_audible_alerts_are_enabled
                                :
                                    strings.settings_global_driver_alerts_audible_alerts_are_disabled
                                }</label>
                                {this.props.audioNotificationsAvailabilityPending ? <ProgressOverlay /> : null }
                            </div>
                            <div className="error-message">
                                {this.props.audioNotificationsAvailabilityErrorMessage}
                            </div>
                        </div>
                        {!this.props.audioNotificationsAvailabilityEnabled &&
                            <div>
                                <div className="alert alert-dark setting-locked" role="alert">{strings.settings_enabled_river_alerts_for_per_event_settings}</div>
                            </div>
                        }
                        <div className='setting-group-horiz'>
                            <div className={this.props.audioNotificationsExpandReferenceIsExpanded ? "subgroup" : "subgroup collapsed"}>
                                <h5 className="subgroup-header">
                                    <button disabled={true}
                                        className="settings-disclosure-wrapper">
                                        {strings.settings_global_driver_alerts_audio_alert_type_header}
                                        <i className="material-icons disclosure">keyboard_arrow_up</i>
                                    </button>
                                </h5>
                                <div>{strings.settings_global_driver_alerts_audio_alert_type_description}</div>
                                <div className="audio-alert-table-wrapper">
                                    <div className="audio-alert-table">
                                        <div className="audio-alert-row-header">
                                            <div className="audio-alert-event-name">{strings.settings_global_driver_alerts_audio_alert_event}</div>
                                            <div className="audio-alert-radio-cell">{strings.settings_global_driver_alerts_audio_alert_none}</div>
                                            <div className="audio-alert-radio-cell">{strings.settings_global_driver_alerts_audio_alert_beep}</div>
                                            <div className="audio-alert-radio-cell">{strings.settings_global_driver_alerts_audio_alert_voice}</div>
                                            <div className="visual-alert-switch-cell">{strings.settings_global_driver_alerts_visual_alert}</div>
                                            <div></div>
                                        </div>
                                        {sortedEvents.map(event => this.renderAudioAlertRow(event))}
                                    </div>
                                </div>
                            </div>
                            {!this.props.audioNotificationsAvailabilityEnabled || this.props.audioNotificationsAvailabilityPending ? <div className="disabled-overlay" /> : null}
                        </div>
                    </div>
                }
            </div>
        );
    }
}